import ApiService from "@/api/ApiService";
import LabelWrapper from "@/components/root/LabelWrapper";
import ManyToOneSelect from "@/shared/forms/ManyToOneSelect";
import { usePersistantStore } from "@/stores/global";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { deDE } from "@mui/x-date-pickers/locales";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import { forwardRef, useImperativeHandle, useState } from "react";

export default forwardRef(function EditPositionDialog({}: {}, ref) {
  const [variant, setVariant] = useState("PROJECT");
  const [resourceId, setResourceId] = useState(null);
  const [project, setProject] = useState(0);
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [specialPlace, setSpecialPlace] = useState(0);
  const [freeText, setFreeText] = useState("");

  useImperativeHandle(ref, () => ({
    open: (r: number, p: number = 0, s: number = 0, d: string = dayjs().format("YYYY-MM-DD")) => {
      setResourceId(r);
      setProject(p);
      setDate(d);
      setSpecialPlace(s < 0 ? 0 : s);
      setVariant(p !== 0 ? "PROJECT" : "SPECIAL");
    },
  }));

  const queryClient = useQueryClient();

  const region = usePersistantStore((x) => x.region);

  const saveMutation = useMutation<any, AxiosResponse, any>({
    mutationFn: ApiService.setPosition(),
    onSuccess: (answer, vars) => {
      queryClient.invalidateQueries({
        predicate: (x) => x.queryKey[0] === "getCalendar",
      });
      setResourceId(null);
    },
  });

  return (
    <LocalizationProvider
      adapterLocale="de"
      dateAdapter={AdapterDayjs}
      localeText={
        deDE.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <Dialog
        open={!!resourceId}
        maxWidth="lg"
        fullWidth
        onClose={() => setResourceId(null)}
      >
        <DialogTitle
          sx={{
            p: 1,
            px: 2,
            background: "#002169",
            color: "white",
            borderBottom: "1px solid rgba(255,255,255,0.5)",
          }}
        >
          Position melden
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <LabelWrapper label="Variante">
            <ToggleButtonGroup
              orientation="horizontal"
              exclusive
              value={variant}
              onChange={(_, variant) => setVariant(variant)}
            >
              <ToggleButton value={"PROJECT"}>Projekt</ToggleButton>
              <ToggleButton value={"SPECIAL"}>Fester Ort</ToggleButton>
              <ToggleButton value={"FREE"}>Freitext</ToggleButton>
            </ToggleButtonGroup>
          </LabelWrapper>
          {variant === "PROJECT" ? (
            <LabelWrapper label="Projekt">
              <ManyToOneSelect
                showWithoutChoice
                remoteFilter={
                  !!region
                    ? { region: ["region", region], active: true }
                    : { active: true }
                }
                displayFunction={(o) =>
                  !!o ? o.name + " (" + o.number + ")" : ""
                }
                remoteFilterReady={true}
                placeholder="Bitte wählen..."
                entity={"project"}
                currentId={project}
                setId={setProject}
              />
            </LabelWrapper>
          ) : variant === "SPECIAL" ? (
            <LabelWrapper label="Fester Ort">
              <ManyToOneSelect
                showWithoutChoice
                remoteFilter={!!region ? { region: ["region", region] } : {}}
                displayFunction={(o) => (!!o ? o.name : "")}
                remoteFilterReady={true}
                placeholder="Bitte wählen..."
                entity={"specialPlace"}
                currentId={specialPlace}
                setId={setSpecialPlace}
              />
            </LabelWrapper>
          ) : (
            <LabelWrapper label="Freier Text">
              <TextField
                size="small"
                fullWidth
                value={freeText}
                autoFocus
                placeholder="Freier Text"
                multiline
                onChange={(e) => {
                  setFreeText((e.target as HTMLInputElement).value);
                }}
              />
            </LabelWrapper>
          )}
          <LabelWrapper label="Datum">
            <DatePicker
              {...{
                slotProps: { textField: { size: "small" } },
                value: dayjs(date, "YYYY-MM-DD"),
                onChange: (val) =>
                  !!val ? setDate(val.format("YYYY-MM-DD")) : null,
                displayWeekNumber: true,
              }}
            />
          </LabelWrapper>
        </DialogContent>
        <DialogActions
          sx={{ borderTop: "1px solid #aaa", justifyContent: "space-between" }}
        >
          <Button onClick={() => setResourceId(null)}>Abbrechen</Button>
          <LoadingButton
            variant="outlined"
            disabled={(variant === "PROJECT" && project === 0) || (variant === "SPECIAL" && specialPlace === 0) || (variant === "FREE" && freeText.length === 0)}
            color="primary"
            loading={saveMutation.status === "pending"}
            onClick={() => {
              saveMutation.mutate({
                specialPlace,
                variant,
                project,
                freeText,
                date,
                resourceId,
              });
            }}
          >
            OK
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
});
